import {QuestionaryException} from '../exceptions';
import Messages from './messages';
import _ from "underscore";

type Config = { [name:string]:string };


export class ValidateResult {
    valid: boolean = true;
    messages: string[];

    static okResult(): ValidateResult {
        return new ValidateResult(true, []);
    }
    static errorResult(message?: string): ValidateResult {
        return message ? new ValidateResult(false, [message]) : new ValidateResult(false, []);
    }
    constructor(valid: boolean, messages: string[]) {
        this.valid = valid;
        this.messages = _.uniq(messages);
    }

    createMerged(result: ValidateResult): ValidateResult {
        return new ValidateResult(this.valid && result.valid, this.messages.concat(result.messages));
    }

}

export interface ContentType {
    validate(value: string): ValidateResult;
    type(): string;
}

export class ContentTypeFactory {
    static fromArray(contentTypes: ContentType[]): ContentType {
        if (contentTypes.length > 1) {
            throw new QuestionaryException("More than one contentType found");
        }
        return contentTypes.length === 0 ? new None() : contentTypes[0];
    }

    static create(config: Config, messages: Messages): ContentType {
        let contentType: ContentType;
        switch (config.type) {
            case "UNCHECKED":
                contentType = new None(); break;
            case "NUMERIC":
                contentType = new Numeric(messages); break;
            case "NUMERIC_RANGE":
                contentType = new NumericRange(config, messages); break;
            case "NUMERIC_DECIMAL":
                contentType = new NumericDecimal(messages); break;
            case "STRING_RANGE":
                contentType = new StringRange(config, messages); break;
            case "REGEXP":
                contentType = new Regexp(config, messages); break;
            case "EMAIL":
                contentType = new Email(messages); break;
            case "DATE":
                contentType = new DateType(messages); break;
            default:
                throw new QuestionaryException(`Unsupported contentType of type '${config.type}'`);
        }
        return new class {
            validate(value: string) {
                let result = contentType.validate(value);
                return config.validateMessage && !result.valid ? ValidateResult.errorResult(config.validateMessage) : result;
                // }
                // if( config.validateMessage && !result.valid ) {
                //     return ValidateResult.errorResult(config.validateMessage);
                // }
                // return result;
            };

            type(): string {
                return 'text';
            }
        }
    }
}
function isNumber(value: string) {
    return /^[0-9]+$/.test(value);
}

class None implements ContentType {
    validate(value: string): ValidateResult {
        return ValidateResult.okResult();
    }
    type(): string {
        return 'text';
    }
}

class Numeric implements ContentType {
    private messages: Messages;
    constructor(messages: Messages) {
        this.messages = messages;
    }

    validate(value: string): ValidateResult {
        return isNumber(value) ? ValidateResult.okResult() : ValidateResult.errorResult(this.messages.get('valuesCheckNumeric', {}));
    }

    type(): string {
        return 'number';
    }
}

class NumericDecimal implements ContentType {
    private messages: Messages;
    constructor(messages: Messages) {
        this.messages = messages;
    }

    validate(value: string): ValidateResult {
        return /^[0-9]+([.,][0-9]+)?$/.test(value) ? ValidateResult.okResult() : ValidateResult.errorResult(this.messages.get('valuesCheckNumericDecimal', {}));
    }

    type(): string {
        return 'number';
    }
}

class NumericRange implements ContentType {
    private rangeMin: number;
    private rangeMax: number;
    private messages: Messages;

    constructor(config: Config, messages: Messages) {
        this.rangeMin = parseInt(config.rangeMin, 10);
        this.rangeMax = parseInt(config.rangeMax, 10);
        this.messages = messages;
        this.checkConsistency();
    }

    validate(value: string): ValidateResult {
        if (isNumber(value)) {
            const number = parseInt(value, 10);
            if( number >= this.rangeMin && number <= this.rangeMax ) {
                return ValidateResult.okResult();
            }
        }
        return ValidateResult.errorResult(this.messages.get('valuesCheckNumericRange', {rangeMin: '' + this.rangeMin, rangeMax: '' + this.rangeMax}));
    }

    private checkConsistency() {
        if( isNaN(this.rangeMin) || isNaN(this.rangeMax) ) {
            throw new QuestionaryException(`NumericRange bad params`);
        }
    }

    type(): string {
        return 'number';
    }
}

class StringRange implements ContentType {
    private rangeMin: number;
    private rangeMax: number;
    private messages: Messages;

    constructor(config: Config, messages: Messages) {
        this.rangeMin = parseInt(config.rangeMin, 10);
        this.rangeMax = parseInt(config.rangeMax, 10);
        this.messages = messages;
        this.checkConsistency();
    }

    validate(value: string): ValidateResult {
        if( value.length >= this.rangeMin && value.length <= this.rangeMax ) {
            return ValidateResult.okResult();
        }
        return ValidateResult.errorResult(this.messages.get('valuesCheckStringRange', {rangeMin: '' + this.rangeMin, rangeMax: '' + this.rangeMax}));
    }

    private checkConsistency() {
        if( isNaN(this.rangeMin) || isNaN(this.rangeMax) ) {
            throw new QuestionaryException(`StringRange bad params`);
        }
    }

    type(): string {
        return 'text';
    }
}

class Email implements ContentType {
    private messages: Messages;
    constructor(messages: Messages) {
        this.messages = messages;
    }

    validate(value: string): ValidateResult {
        return /^\S+@\S+\.\S+$/.test(value) ? ValidateResult.okResult() : ValidateResult.errorResult(this.messages.get('valuesCheckEmail', {}));
    }

    type(): string {
        return 'email';
    }
}

class Regexp implements ContentType {
    private messages: Messages;
    private regexp: RegExp;
    private pattern: string;
    constructor(config: Config, messages: Messages) {
        this.messages = messages;
        this.pattern = config.regexpString;
        this.regexp = new RegExp(config.regexpString);
        this.checkConsistency();
    }

    validate(value: string): ValidateResult {
        return this.regexp.test(value) ? ValidateResult.okResult() : ValidateResult.errorResult(this.messages.get('valuesCheckRegexp', {pattern: this.pattern}));
    }

    private checkConsistency() {
        if( _.isUndefined(this.pattern) ) {
            throw new QuestionaryException(`Regexp bad params`);
        }
    }

    type(): string {
        return 'text';
    }
}

class DateType implements ContentType {
    private messages: Messages;
    constructor(messages: Messages) {
        this.messages = messages;
    }

    validate(value: string): ValidateResult {
        return this.isDateValid(value) ? ValidateResult.okResult() : ValidateResult.errorResult(this.messages.get('valuesCheckDate', {}));
    }

    private isDateValid(dateString: string) {
        if( !/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateString) ) {
            return false;
        }
        const bits = dateString.split('.');
        const d = new Date(<any>bits[2], <any>bits[1] - 1, <any>bits[0]);
        /* tslint:disable */
        return d && <any>(d.getMonth() + 1) == bits[1];
        /* tslint:enable */
    }

    type(): string {
        return 'date';
    }
}
